/**
 * ForgotPassword.tsx — asks for an email address and sends a one-time reset link. The answer is the same whether or not
 * the address has an account, so this screen cannot be used to find out who works here.
 */
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { authApi, errorText } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { AuthShell } from "@/components/auth/AuthShell";

export function ForgotPassword() {
  const location = useLocation();
  const [email, setEmail] = useState((location.state as { email?: string } | null)?.email ?? "");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await authApi.requestReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(errorText(err, "Could not send the link. Try again."));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <AuthShell title="Check your email">
        <div className="mt-6 flex items-start gap-3 rounded-lg bg-neutral-50 p-4 ring-1 ring-inset ring-neutral-200">
          <MailCheck className="mt-0.5 size-5 shrink-0 text-success-600" aria-hidden />
          <p className="text-sm text-neutral-700">
            If <span className="font-medium text-neutral-900">{email}</span> has an account, a link to choose a new password is on its way. It works once and expires in 1 hour.
          </p>
        </div>
        <p className="mt-4 text-sm text-neutral-500">Nothing after a few minutes? Check spam, or ask a manager to send you a new link.</p>
        <Link to="/login" className="mt-8 inline-block text-sm font-medium text-accent-700 hover:underline">Back to sign in</Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Forgot your password?" subtitle="Enter your email and we will send you a link to choose a new one.">
      {error && <Alert tone="danger" className="mt-6">{error}</Alert>}
      <form onSubmit={submit} className="mt-8 space-y-5">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <Button type="submit" className="w-full" size="lg" loading={busy}>
          {busy ? "Sending…" : "Send me a link"}
        </Button>
      </form>
      <Link to="/login" className="mt-8 inline-block text-sm text-neutral-500 hover:text-neutral-800">Back to sign in</Link>
    </AuthShell>
  );
}
